import { supabase } from '../lib/supabase';

/**
 * Reviewer Service
 * Handles reviewer-specific operations for managing assigned papers and workload
 * Converts between snake_case (database) and camelCase (React)
 */

export const reviewerService = {
  /**
   * Get all assigned papers for a reviewer
   */
  async getAssignedPapers(reviewerId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          id,
          submission_id,
          conference_id,
          overall_score,
          is_draft,
          submitted_at,
          created_at,
          updated_at,
          conference_submissions (
            id, title, abstract, keywords, submitted_at, pdf_url, paper_number,
            user_profiles (first_name, last_name, institution),
            conference_tracks (name)
          ),
          conferences (
            id, name, short_name, paper_deadline, review_deadline
          )
        `)
        ?.eq('reviewer_id', reviewerId)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(review => {
        const submission = review?.conference_submissions;
        const conference = review?.conferences;
        const author = submission?.user_profiles;

        return {
          reviewId: review?.id,
          submissionId: review?.submission_id,
          conferenceId: review?.conference_id,
          conferenceName: conference?.name,
          conferenceShortName: conference?.short_name,
          paperTitle: submission?.title,
          paperAbstract: submission?.abstract,
          paperNumber: submission?.paper_number,
          pdfUrl: submission?.pdf_url,
          keywords: submission?.keywords || [],
          authorInstitution: author?.institution,
          trackName: submission?.conference_tracks?.name,
          submittedAt: submission?.submitted_at,
          assignedAt: review?.created_at,
          reviewDeadline: conference?.review_deadline || conference?.paper_deadline,
          overallScore: review?.overall_score,
          isDraft: review?.is_draft,
          completedAt: review?.submitted_at,
          status: !review?.is_draft ? 'completed' : review?.overall_score ? 'in-progress' : 'pending',
          progress: !review?.is_draft ? 100 : review?.overall_score ? 50 : 0
        };
      }) || [];
    } catch (error) {
      console.error('Get assigned papers error:', error);
      throw error;
    }
  },

  /**
   * Get reviewer workload statistics
   */
  async getWorkloadStats(reviewerId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          id,
          is_draft,
          submitted_at,
          created_at,
          conferences (id, name, review_deadline, paper_deadline)
        `)
        ?.eq('reviewer_id', reviewerId);

      if (error) throw error;

      const total = data?.length || 0;
      const completed = data?.filter(r => !r?.is_draft)?.length || 0;
      const pending = total - completed;

      // Calculate overdue reviews
      const now = new Date();
      const overdue = data?.filter(r => {
        if (!r?.is_draft) return false;
        const deadline = new Date(r?.conferences?.review_deadline || r?.conferences?.paper_deadline);
        return deadline < now;
      })?.length || 0;

      // Calculate average review time for completed reviews
      const completedReviews = data?.filter(r => !r?.is_draft && r?.submitted_at);
      let avgReviewTime = 0;
      if (completedReviews?.length > 0) {
        const totalTime = completedReviews?.reduce((sum, r) => {
          const assigned = new Date(r?.created_at);
          const completed = new Date(r?.submitted_at);
          return sum + (completed - assigned);
        }, 0);
        avgReviewTime = Math.round(totalTime / completedReviews?.length / (1000 * 60 * 60 * 24)); // days
      }

      // Group by conference for workload distribution
      const conferenceWorkload = {};
      data?.forEach(review => {
        const confId = review?.conferences?.id;
        const confName = review?.conferences?.name;
        if (!conferenceWorkload?.[confId]) {
          conferenceWorkload[confId] = {
            conferenceId: confId,
            conferenceName: confName,
            total: 0,
            completed: 0,
            pending: 0
          };
        }
        conferenceWorkload[confId].total++;
        if (review?.is_draft) {
          conferenceWorkload[confId].pending++;
        } else {
          conferenceWorkload[confId].completed++;
        }
      });

      return {
        total,
        completed,
        pending,
        overdue,
        avgReviewTime,
        completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
        conferenceWorkload: Object.values(conferenceWorkload)
      };
    } catch (error) {
      console.error('Get workload stats error:', error);
      throw error;
    }
  },

  /**
   * Get upcoming deadlines for reviewer
   */
  async getUpcomingDeadlines(reviewerId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          id,
          is_draft,
          conference_submissions (title),
          conferences (name, review_deadline, paper_deadline)
        `)
        ?.eq('reviewer_id', reviewerId)
        ?.eq('is_draft', true)
        ?.order('created_at', { ascending: false });

      if (error) throw error;

      const now = new Date();
      const deadlines = data?.map(review => {
        const deadline = new Date(review?.conferences?.review_deadline || review?.conferences?.paper_deadline);
        const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));

        return {
          id: review?.id,
          title: review?.conference_submissions?.title,
          conference: review?.conferences?.name,
          deadline: review?.conferences?.review_deadline || review?.conferences?.paper_deadline,
          daysLeft,
          urgency: daysLeft <= 3 ? 'critical' : daysLeft <= 7 ? 'high' : 'normal'
        };
      }) || [];

      // Sort by urgency (closest deadlines first)
      return deadlines?.sort((a, b) => a?.daysLeft - b?.daysLeft);
    } catch (error) {
      console.error('Get upcoming deadlines error:', error);
      throw error;
    }
  },

  /**
   * Update review progress
   */
  async updateReviewProgress(reviewId, progressData) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.update({
          overall_score: progressData?.overallScore,
          is_draft: progressData?.isDraft !== false,
          submitted_at: progressData?.isDraft === false ? new Date()?.toISOString() : null,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', reviewId)
        ?.select()
        ?.single();

      if (error) throw error;

      return {
        id: data?.id,
        overallScore: data?.overall_score,
        isDraft: data?.is_draft,
        submittedAt: data?.submitted_at
      };
    } catch (error) {
      console.error('Update review progress error:', error);
      throw error;
    }
  },

  /**
   * Get review details
   */
  async getReviewDetails(reviewId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          *,
          conference_submissions (
            id, title, abstract, keywords, pdf_url, submitted_at,
            user_profiles (first_name, last_name, email, institution),
            conference_tracks (name)
          ),
          conferences (
            id, name, paper_deadline, review_deadline
          )
        `)
        ?.eq('id', reviewId)
        ?.single();

      if (error) throw error;

      const submission = data?.conference_submissions;
      const conference = data?.conferences;
      const author = submission?.user_profiles;

      return {
        reviewId: data?.id,
        submissionId: data?.submission_id,
        conferenceId: data?.conference_id,
        conferenceName: conference?.name,
        paperTitle: submission?.title,
        paperAbstract: submission?.abstract,
        keywords: submission?.keywords || [],
        pdfUrl: submission?.pdf_url,
        authorName: `${author?.first_name} ${author?.last_name}`,
        authorEmail: author?.email,
        authorInstitution: author?.institution,
        trackName: submission?.conference_tracks?.name,
        submittedAt: submission?.submitted_at,
        reviewDeadline: conference?.review_deadline || conference?.paper_deadline,
        overallScore: data?.overall_score,
        isDraft: data?.is_draft,
        completedAt: data?.submitted_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error('Get review details error:', error);
      throw error;
    }
  },

  /**
   * Calculate estimated completion time based on paper length
   */
  estimateCompletionTime(paperLength) {
    // Rough estimate: 1 page = 15 minutes review time
    const estimatedMinutes = (paperLength || 10) * 15;
    const hours = Math.floor(estimatedMinutes / 60);
    const minutes = estimatedMinutes % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  },

  /**
   * Get review for a specific submission that the reviewer is assigned to
   * Returns complete review object with answers
   */
  async getReviewForSubmission(submissionId, reviewerId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.select(`
          *,
          conference_submissions (
            id, title, abstract, keywords, pdf_url, submitted_at,
            user_profiles (first_name, last_name, email, institution),
            conference_tracks (name)
          ),
          conferences (
            id, name, paper_deadline, review_deadline
          )
        `)
        ?.eq('submission_id', submissionId)
        ?.eq('reviewer_id', reviewerId)
        ?.single();

      if (error && error?.code !== 'PGRST116') throw error; // PGRST116 = not found
      if (!data) return null;

      const submission = data?.conference_submissions;
      const conference = data?.conferences;
      const author = submission?.user_profiles;

      // Fetch review answers using criteriaAnswersService
      const { criteriaAnswersService } = await import('./criteriaAnswersService');
      const answersData = await criteriaAnswersService?.getAnswersByReview(submissionId, reviewerId, true);

      return {
        reviewId: data?.id,
        submissionId: data?.submission_id,
        conferenceId: data?.conference_id,
        conferenceName: conference?.name,
        paperTitle: submission?.title,
        paperAbstract: submission?.abstract,
        keywords: submission?.keywords || [],
        pdfUrl: submission?.pdf_url,
        authorName: `${author?.first_name} ${author?.last_name}`,
        authorEmail: author?.email,
        authorInstitution: author?.institution,
        trackName: submission?.conference_tracks?.name,
        submittedAt: submission?.submitted_at,
        reviewDeadline: conference?.review_deadline || conference?.paper_deadline,
        overallScore: data?.overall_score,
        detailedFeedback: data?.detailed_feedback,
        strengths: data?.strengths,
        weaknesses: data?.weaknesses,
        decision: data?.decision,
        confidenceLevel: data?.confidence_level,
        isDraft: data?.is_draft,
        completedAt: data?.submitted_at,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        criteriaAnswers: answersData?.map
      };
    } catch (error) {
      console.error('Get review for submission error:', error);
      throw error;
    }
  },

  /**
   * Save review draft with all answers
   */
  async saveDraft(reviewData) {
    try {
      const { submissionId, reviewerId, conferenceId, overallScore, detailedFeedback, strengths, weaknesses, decision, confidenceLevel, criteriaAnswers } = reviewData;

      if (!submissionId || !reviewerId || !conferenceId) {
        throw new Error('Missing required fields: submissionId, reviewerId, conferenceId');
      }

      // First, save or update the main review record
      const { data: reviewRecord, error: reviewError } = await supabase
        ?.from('submission_reviews')
        ?.update({
          overall_score: overallScore || 5,
          detailed_feedback: detailedFeedback || '',
          strengths: strengths || '',
          weaknesses: weaknesses || '',
          decision: decision || null,
          confidence_level: confidenceLevel || null,
          is_draft: true,
          updated_at: new Date().toISOString()
        })
        ?.eq('submission_id', submissionId)
        ?.eq('reviewer_id', reviewerId)
        ?.select()
        ?.single();

      if (reviewError) throw reviewError;

      // Save criteria answers
      const { criteriaAnswersService } = await import('./criteriaAnswersService');
      if (criteriaAnswers && Object.keys(criteriaAnswers).length > 0) {
        await criteriaAnswersService?.saveAnswers(submissionId, reviewerId, criteriaAnswers);
      }

      return {
        success: true,
        reviewId: reviewRecord?.id,
        message: 'Draft saved successfully'
      };
    } catch (error) {
      console.error('Save draft error:', error);
      throw error;
    }
  },

  /**
   * Submit completed review (final submission)
   */
  async submitReview(reviewData) {
    try {
      const { submissionId, reviewerId, conferenceId, overallScore, detailedFeedback, strengths, weaknesses, decision, confidenceLevel, criteriaAnswers } = reviewData;

      if (!submissionId || !reviewerId || !conferenceId) {
        throw new Error('Missing required fields: submissionId, reviewerId, conferenceId');
      }

      // Validate required scores
      if (!overallScore || overallScore < 1 || overallScore > 10) {
        throw new Error('Overall score must be between 1 and 10');
      }

      // Save or update the review record as final submission
      const now = new Date().toISOString();
      const { data: reviewRecord, error: reviewError } = await supabase
        ?.from('submission_reviews')
        ?.update({
          overall_score: overallScore,
          detailed_feedback: detailedFeedback || '',
          strengths: strengths || '',
          weaknesses: weaknesses || '',
          decision: decision || null,
          confidence_level: confidenceLevel || null,
          is_draft: false,
          submitted_at: now,
          updated_at: now
        })
        ?.eq('submission_id', submissionId)
        ?.eq('reviewer_id', reviewerId)
        ?.select()
        ?.single();

      if (reviewError) throw reviewError;

      // Save criteria answers
      const { criteriaAnswersService } = await import('./criteriaAnswersService');
      if (criteriaAnswers && Object.keys(criteriaAnswers).length > 0) {
        await criteriaAnswersService?.saveAnswers(submissionId, reviewerId, criteriaAnswers);
      }

      // Send confirmation email to reviewer (non-blocking)
      try {
        const { notificationService } = await import('./notificationService');
        const [{ data: reviewer }, { data: submission }] = await Promise.all([
          supabase?.from('user_profiles')?.select('first_name, last_name, email')?.eq('id', reviewerId)?.single(),
          supabase?.from('conference_submissions')?.select('title, conference_id, conferences(name)')?.eq('id', submissionId)?.single()
        ]);
        if (reviewer?.email && submission?.title) {
          const reviewerName = `${reviewer?.first_name || ''} ${reviewer?.last_name || ''}`.trim();
          const conferenceName = submission?.conferences?.name || 'the conference';
          await notificationService?.sendReviewSubmittedEmail(reviewer.email, reviewerName, submission.title, conferenceName, submission?.conference_id);
        }
      } catch (emailErr) {
        console.warn('Review confirmation email failed (non-critical):', emailErr?.message);
      }

      return {
        success: true,
        reviewId: reviewRecord?.id,
        submittedAt: reviewRecord?.submitted_at,
        message: 'Review submitted successfully'
      };
    } catch (error) {
      console.error('Submit review error:', error);
      throw error;
    }
  },

  /**
   * Get review answers for a submission and reviewer
   * Helper method
   */
  async getReviewAnswers(submissionId, reviewerId) {
    try {
      const { criteriaAnswersService } = await import('./criteriaAnswersService');
      return await criteriaAnswersService?.getAnswersByReview(submissionId, reviewerId, false);
    } catch (error) {
      console.error('Get review answers error:', error);
      throw error;
    }
  },

  /**
   * Check if reviewer has already submitted a review for this paper
   */
  async hasSubmittedReview(submissionId, reviewerId) {
    try {
      const { data, error } = await supabase
        ?.from('submission_reviews')
        ?.select('id, is_draft')
        ?.eq('submission_id', submissionId)
        ?.eq('reviewer_id', reviewerId)
        ?.maybeSingle();

      if (error) throw error;

      return {
        hasReview: !!data,
        isDraft: data?.is_draft,
        reviewId: data?.id
      };
    } catch (error) {
      console.error('Check submitted review error:', error);
      throw error;
    }
  }
};

export default reviewerService;